import { useCallback, useMemo, useState } from "react"

/**
 * Checkbox multi-select state for a list panel. Lifted to the page level so
 * the sibling detail panel can react to the selection too.
 *
 * A caller that already has the row in hand when it is checked may pass it
 * along; the sibling panel then renders the selection straight from those
 * snapshots instead of resolving the ids again. That only works where the
 * selection cannot outlive the rows that produced it (the inquiry drafts:
 * leaving the draft tab clears it). The master-data panels deliberately pass
 * no snapshot — their selection survives filter changes, so it can hold ids
 * that were never loaded together, and it is resolved server-side instead.
 */
export function useRowMultiSelect<T extends { id: string } = { id: string }>() {
  // One source of truth: the keys ARE the selection, the values are optional
  // snapshots. Two parallel states would be free to drift.
  const [rowsById, setRowsById] = useState<Map<string, T | undefined>>(
    new Map(),
  )

  const selectedIds = useMemo(() => new Set(rowsById.keys()), [rowsById])

  /** The snapshots, in selection order. Empty for callers that pass none. */
  const selectedRows = useMemo(
    () => [...rowsById.values()].filter((row): row is T => row !== undefined),
    [rowsById],
  )

  const toggleSelected = useCallback((id: string, row?: T) => {
    setRowsById((prev) => {
      const next = new Map(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.set(id, row)
      }
      return next
    })
  }, [])

  const removeIds = useCallback((ids: string[]) => {
    setRowsById((prev) => {
      const next = new Map(prev)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  // Replace the whole selection with the given rows (used by a select-all control).
  const selectAll = useCallback((rows: T[]) => {
    setRowsById(new Map(rows.map((row) => [row.id, row])))
  }, [])

  const clearAll = useCallback(() => {
    setRowsById(new Map())
  }, [])

  return {
    selectedIds,
    selectedRows,
    toggleSelected,
    removeIds,
    selectAll,
    clearAll,
  }
}
